import clsx from "clsx";
import { Button, ButtonVariant, getButtonStyle } from "components/Form/Button";
import { Layout } from "components/Layout";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AppRoutes } from "types";
import { Explanation, ExplanationIcons } from "components/Explanation";
import { GalleryPreview } from "components/GalleryPreview";
import { UploadContext, UploadType } from "contexts/UploadContext";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAcceptedFileTypes } from "lib/upload-files";

export function VideoUpload() {
  const [init, setInit] = useState<boolean>(false);
  const location = useLocation();

  const taskId = location.state?.taskId;
  const taskName = location.state?.taskName;

  const {
    files: videos,
    setFiles: setVideos,
    resetFiles: resetVideos,
    upload,
  } = useContext(UploadContext);
  const { config } = useContext(CollectionDetailsContext);
  const privacyModal = usePrivacyModal();
  const navigate = useNavigate();

  const handleChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const files = Array.from(event.target.files);
      const videoFiles = files.filter((file) => file.type.startsWith("video/"));

      if (videoFiles.length !== files.length) {
        toast.warn("Only video files are accepted for this task");
      }

      if (videoFiles.length > 0) {
        setVideos(videoFiles);
      }
    },
    [setVideos]
  );

  useEffect(() => {
    if (!config || privacyModal.accepted === null) {
      return;
    }

    if (privacyModal.accepted === false) {
      navigate(AppRoutes.home);
      return;
    }

    if (!taskId) {
      navigate(AppRoutes.home);
      return;
    }

    setInit(true);
  }, [config, navigate, privacyModal.accepted, taskId]);

  if (!init) {
    return null;
  }

  return (
    <Layout
      className="space-y-8"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          {videos.length <= 0 && (
            <>
              <label className={clsx(getButtonStyle(), "cursor-pointer")}>
                <input
                  type="file"
                  accept={getAcceptedFileTypes("video")}
                  multiple
                  onChange={handleChangeInput}
                  hidden
                />
                <span>Choose videos</span>
              </label>
              <button
                onClick={() => navigate(AppRoutes.home)}
                className="text-sm text-858494 underline hover:text-191c20 transition-colors"
              >
                Back to Home
              </button>
            </>
          )}
          {videos.length > 0 && (
            <>
              <Button onClick={() => upload(UploadType.REGULAR, taskId)}>
                Upload
              </Button>
              <Button onClick={resetVideos} variant={ButtonVariant.FLAT}>
                Choose another
              </Button>
            </>
          )}
        </>
      }
    >
      <h1 className="text-24 font-semibold text-center">
        {videos.length <= 0 && (taskName ? `${taskName}` : "Choose videos")}
        {videos.length > 0 && "Preview videos"}
      </h1>

      {videos.length > 0 && (
        <div className="text-858494 leading-5 text-center">
          Review videos and labels carefully. <br />
          Correct any mismatches before uploading.
        </div>
      )}

      {videos.length <= 0 && (
        <Explanation
          items={[
            {
              icon: ExplanationIcons.note,
              title: "Input collection details",
              description: (
                <p>
                  Add any relevant information you want to upload along with the
                  videos. Ensure all details entered apply consistently across
                  all videos uploaded in the session.
                </p>
              ),
            },
            {
              icon: ExplanationIcons.selfie,
              title: "Select videos to upload",
              description: (
                <p>
                  Tap on the button to select video files you intend to upload.
                  Preview them and confirm the upload.
                </p>
              ),
            },
            {
              icon: ExplanationIcons.bulb,
              title: "Requirements",
              description: (
                <ul className="leading-[18px] list-disc">
                  <li>Videos must be in portrait orientation throughout.</li>
                  <li>Limit video length to 15 seconds max.</li>
                  <li>
                    File sizes should be at least 10 KB and no more than 250 MB.
                  </li>
                </ul>
              ),
            },
          ]}
        />
      )}
      {videos.length > 0 && (
        <GalleryPreview
          isRemovable
          showLabels={
            config?.labels &&
            Object.keys(config.labels).some((key) => key !== "devices")
          }
        />
      )}
    </Layout>
  );
}
